import { z } from 'zod';
import { BusinessCodeSchema, EnteredNameSchema, RecordIdSchema } from './identifiers';
import { LocalizedLabelSchema } from './localized';
import { DecimalStringSchema, MoneySchema } from './money';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Banks, treasury and reconciliation — `FIN-BANK`, `FIN-CASH`, `FIN-REPORT-004` (BMP-2 package 3,
 * ADR-0034, ADR-0036).
 *
 * **The product records money; it never moves it.** A deposit, a transfer or a payment here is the
 * evidence of something done at a bank or a cash desk, entered by a person and approved where a policy
 * governs it (`G-01`, ADR-0034 §5). No bank is connected and no credential is held.
 *
 * Three rules shape everything:
 *
 * 1. **A treasury account's book balance is the sum of its recorded movements**, kept on the account
 *    and changed only inside the movement's transaction. A movement is reversed by a contra-movement,
 *    never edited or deleted (ADR-0009).
 * 2. **Account numbers and IBANs are encrypted at rest** and only their last four characters are ever
 *    returned by an ordinary read (`FIN-BANK-001`). Seeing the whole value is a separate, audited
 *    request with its own permission and a second factor. No format is assumed: what a bank prints is
 *    stored, without its spaces.
 * 3. **The ledger hears about every movement through the posting rules** (`BD-18`): nothing here names
 *    an account. A movement whose rule is not configured is queued, and the treasury operation still
 *    succeeds.
 */

/* ----------------------------------------------------------------------- banks */

export const TREASURY_RECORD_STATES = ['active', 'inactive'] as const;
export const TreasuryRecordStateSchema = z.enum(TREASURY_RECORD_STATES);
export type TreasuryRecordState = z.infer<typeof TreasuryRecordStateSchema>;

export const BankBranchSchema = z.strictObject({
  code: BusinessCodeSchema,
  name: LocalizedLabelSchema,
  state: TreasuryRecordStateSchema,
});
export type BankBranch = z.infer<typeof BankBranchSchema>;

/** A bank's own code as it prints it (a SWIFT/BIC or a local code). Stored verbatim, not validated. */
const BankCodeTextSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9]{1,20}$/, { message: 'BANK_CODE_EXPECTED' });

export const BankSchema = z.strictObject({
  bankId: RecordIdSchema,
  code: BusinessCodeSchema,
  name: LocalizedLabelSchema,
  swiftCode: z.string().optional(),
  branches: z.array(BankBranchSchema),
  state: TreasuryRecordStateSchema,
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Bank = z.infer<typeof BankSchema>;

export const CreateBankSchema = z.strictObject({
  code: BusinessCodeSchema,
  name: LocalizedLabelSchema,
  swiftCode: BankCodeTextSchema.optional(),
});
export type CreateBank = z.infer<typeof CreateBankSchema>;

export const UpdateBankSchema = z
  .strictObject({
    name: LocalizedLabelSchema.optional(),
    swiftCode: BankCodeTextSchema.optional(),
    expectedVersion: z.number().int().positive(),
  })
  .refine((value) => value.name !== undefined || value.swiftCode !== undefined, {
    message: 'NOTHING_TO_UPDATE',
  });
export type UpdateBank = z.infer<typeof UpdateBankSchema>;

export const AddBankBranchSchema = z.strictObject({
  code: BusinessCodeSchema,
  name: LocalizedLabelSchema,
  expectedVersion: z.number().int().positive(),
});
export type AddBankBranch = z.infer<typeof AddBankBranchSchema>;

export const TreasuryLifecycleSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type TreasuryLifecycle = z.infer<typeof TreasuryLifecycleSchema>;

export const BankListSchema = z.strictObject({ items: z.array(BankSchema) });

/* ------------------------------------------------------------- treasury accounts */

export const TREASURY_ACCOUNT_KINDS = ['bank', 'cashbox', 'pettyCash'] as const;
export const TreasuryAccountKindSchema = z.enum(TREASURY_ACCOUNT_KINDS);
export type TreasuryAccountKind = z.infer<typeof TreasuryAccountKindSchema>;

/**
 * An account number or IBAN as the bank prints it: letters, digits, spaces and dashes. Spaces and
 * dashes are dropped and letters upper-cased before storage; **no country format or check digit is
 * assumed** (no bank-account format is approved).
 */
export const BankIdentifierInputSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9][A-Za-z0-9 -]{2,46}[A-Za-z0-9]$/, { message: 'BANK_IDENTIFIER_EXPECTED' });

/** The stored form of a bank identifier: no spaces or dashes, upper case. */
export function normalizeBankIdentifier(value: string): string {
  return value.replace(/[\s-]/g, '').toUpperCase();
}

/** What every ordinary read shows of a bank identifier: its last four characters. */
export function maskBankIdentifier(value: string): string {
  const normalized = normalizeBankIdentifier(value);
  return `••••${normalized.slice(-4)}`;
}

/**
 * A bank account's overdraft facility (BD-55, approved 2026-10-07). **Absent, the account may not go
 * below zero** — like every cashbox and petty cash, which never may. Present, money may leave until the
 * balance reaches minus the limit and not one unit further. The reference is the bank's facility letter
 * or agreement as it prints it; the limit is set by an administrator with a second factor, audited.
 */
export const OverdraftFacilitySchema = z.strictObject({
  limit: MoneySchema,
  reference: z.string(),
  setByAccountId: z.string(),
  setAt: InstantSchema,
});
export type OverdraftFacility = z.infer<typeof OverdraftFacilitySchema>;

export const OverdraftInputSchema = z.strictObject({
  limit: MoneySchema.refine(
    (value) => !value.amount.startsWith('-') && !/^0(.0+)?$/.test(value.amount),
    { message: 'POSITIVE_AMOUNT_EXPECTED' },
  ),
  reference: z.string().trim().min(2).max(120),
});
export type OverdraftInput = z.infer<typeof OverdraftInputSchema>;

/**
 * A cashbox's limits (`BD-41`): the most one cash payment may take from it, and the balance it should
 * not hold beyond. Absent — not configured, nothing limited, and the screens say so. A payment above
 * `maxPayment` is refused; a balance above `maxBalance` is allowed and warned (cash must still be
 * receivable), so it can be deposited. Set by an administrator with a second factor, audited.
 */
export const CashLimitsSchema = z.strictObject({
  maxPayment: MoneySchema.optional(),
  maxBalance: MoneySchema.optional(),
  setByAccountId: z.string(),
  setAt: InstantSchema,
});
export type CashLimits = z.infer<typeof CashLimitsSchema>;

const PositiveMoneySchema = MoneySchema.refine(
  (value) => !value.amount.startsWith('-') && !/^0(.0+)?$/.test(value.amount),
  { message: 'POSITIVE_AMOUNT_EXPECTED' },
);
export const CashLimitsInputSchema = z
  .strictObject({
    maxPayment: PositiveMoneySchema.optional(),
    maxBalance: PositiveMoneySchema.optional(),
  })
  .refine((value) => value.maxPayment !== undefined || value.maxBalance !== undefined, {
    message: 'NOTHING_TO_UPDATE',
  });
export type CashLimitsInput = z.infer<typeof CashLimitsInputSchema>;

export const TreasuryAccountSchema = z.strictObject({
  accountId: RecordIdSchema,
  code: BusinessCodeSchema,
  name: LocalizedLabelSchema,
  kind: TreasuryAccountKindSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema.optional(),
  /** The legal entity's currency: one currency per entity (`BD-54`). */
  currency: z.string().regex(/^[A-Z]{3}$/),
  /** The chart account posting rules may use as this account's context account (`BD-37`). */
  ledgerAccountId: RecordIdSchema.optional(),
  /** The person who holds a cashbox or petty cash. Named by a person; never assumed (`SD-07`, `BD-41`). */
  custodianAccountId: z.string().min(1).max(200).optional(),
  bankId: RecordIdSchema.optional(),
  bankBranchCode: BusinessCodeSchema.optional(),
  /** `••••1234` — the whole value is encrypted and returned only by the audited reveal. */
  accountNumberMasked: z.string().optional(),
  ibanMasked: z.string().optional(),
  bookBalance: MoneySchema,
  /** A bank account's approved overdraft facility (`BD-55`); absent — no overdraft. */
  overdraft: OverdraftFacilitySchema.optional(),
  /** A cashbox's or petty cash's limits (`BD-41`); absent — not configured. */
  cashLimits: CashLimitsSchema.optional(),
  /** What may still leave the account: the book balance plus any overdraft limit, never below zero. */
  availableBalance: MoneySchema,
  /** True once an opening balance was recorded; a second one is refused. */
  openingRecorded: z.boolean(),
  state: TreasuryRecordStateSchema,
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type TreasuryAccount = z.infer<typeof TreasuryAccountSchema>;

export const CreateTreasuryAccountSchema = z
  .strictObject({
    legalEntityId: RecordIdSchema,
    branchId: RecordIdSchema.optional(),
    code: BusinessCodeSchema,
    name: LocalizedLabelSchema,
    kind: TreasuryAccountKindSchema,
    ledgerAccountId: RecordIdSchema.optional(),
    custodianAccountId: z.string().min(1).max(200).optional(),
    bankId: RecordIdSchema.optional(),
    bankBranchCode: BusinessCodeSchema.optional(),
    accountNumber: BankIdentifierInputSchema.optional(),
    iban: BankIdentifierInputSchema.optional(),
    overdraft: OverdraftInputSchema.optional(),
  })
  .refine((value) => value.kind !== 'bank' || value.bankId !== undefined, {
    message: 'BANK_REQUIRED',
    path: ['bankId'],
  })
  .refine(
    (value) =>
      value.kind === 'bank' ||
      (value.bankId === undefined &&
        value.bankBranchCode === undefined &&
        value.accountNumber === undefined &&
        value.iban === undefined &&
        value.overdraft === undefined),
    { message: 'BANK_FIELDS_ON_CASH_ACCOUNT', path: ['kind'] },
  );
export type CreateTreasuryAccount = z.infer<typeof CreateTreasuryAccountSchema>;

export const UpdateTreasuryAccountSchema = z
  .strictObject({
    name: LocalizedLabelSchema.optional(),
    ledgerAccountId: RecordIdSchema.optional(),
    custodianAccountId: z.string().min(1).max(200).optional(),
    /** Set or replace the overdraft facility; `null` withdraws it (bank accounts only, `BD-55`). */
    overdraft: OverdraftInputSchema.nullable().optional(),
    /** Set or replace a cashbox's limits; `null` withdraws them (cash accounts only, `BD-41`). */
    cashLimits: CashLimitsInputSchema.nullable().optional(),
    /** Required with an overdraft or limit change: why it was set, changed or withdrawn. */
    reason: z.string().trim().min(3).max(500).optional(),
    expectedVersion: z.number().int().positive(),
  })
  .refine(
    (value) =>
      value.name !== undefined ||
      value.ledgerAccountId !== undefined ||
      value.custodianAccountId !== undefined ||
      value.overdraft !== undefined ||
      value.cashLimits !== undefined,
    { message: 'NOTHING_TO_UPDATE' },
  )
  .refine(
    (value) =>
      (value.overdraft === undefined && value.cashLimits === undefined) ||
      value.reason !== undefined,
    { message: 'REASON_REQUIRED', path: ['reason'] },
  );
export type UpdateTreasuryAccount = z.infer<typeof UpdateTreasuryAccountSchema>;

export const TreasuryAccountQuerySchema = z.strictObject({
  legalEntityId: RecordIdSchema.optional(),
  kind: TreasuryAccountKindSchema.optional(),
  state: TreasuryRecordStateSchema.optional(),
});
export type TreasuryAccountQuery = z.infer<typeof TreasuryAccountQuerySchema>;

export const TreasuryAccountListSchema = z.strictObject({ items: z.array(TreasuryAccountSchema) });

/** The whole identifiers, returned only to the audited reveal. */
export const RevealedBankIdentifiersSchema = z.strictObject({
  accountNumber: z.string().optional(),
  iban: z.string().optional(),
});
export type RevealedBankIdentifiers = z.infer<typeof RevealedBankIdentifiersSchema>;

/* ------------------------------------------------------------------- movements */

/**
 * What a movement records. A person records the first seven; transfers, collections, payments and
 * reversals are written by their own workflows.
 */
export const MOVEMENT_KINDS = [
  'opening',
  'deposit',
  'withdrawal',
  'bankCharge',
  'bankInterest',
  'cashReceipt',
  'cashPayment',
  'transferIn',
  'transferOut',
  'collection',
  'payment',
  'reversal',
] as const;
export const MovementKindSchema = z.enum(MOVEMENT_KINDS);
export type MovementKind = z.infer<typeof MovementKindSchema>;

export const RECORDABLE_MOVEMENT_KINDS = [
  'opening',
  'deposit',
  'withdrawal',
  'bankCharge',
  'bankInterest',
  'cashReceipt',
  'cashPayment',
] as const satisfies readonly MovementKind[];
export type RecordableMovementKind = (typeof RECORDABLE_MOVEMENT_KINDS)[number];

/** Which account kinds a person may record each kind on. A deposit goes to a bank; cash to a box. */
export const MOVEMENT_ACCOUNT_KINDS: Readonly<
  Record<RecordableMovementKind, readonly TreasuryAccountKind[]>
> = {
  opening: ['bank', 'cashbox', 'pettyCash'],
  deposit: ['bank'],
  withdrawal: ['bank'],
  bankCharge: ['bank'],
  bankInterest: ['bank'],
  cashReceipt: ['cashbox', 'pettyCash'],
  cashPayment: ['cashbox', 'pettyCash'],
};

export const MOVEMENT_DIRECTIONS = ['in', 'out'] as const;
export const MovementDirectionSchema = z.enum(MOVEMENT_DIRECTIONS);
export type MovementDirection = z.infer<typeof MovementDirectionSchema>;

/** The direction money moves for each kind (a reversal takes the opposite of what it reverses). */
export const MOVEMENT_DIRECTION: Readonly<
  Record<Exclude<MovementKind, 'reversal'>, MovementDirection>
> = {
  opening: 'in',
  deposit: 'in',
  withdrawal: 'out',
  bankCharge: 'out',
  bankInterest: 'in',
  cashReceipt: 'in',
  cashPayment: 'out',
  transferIn: 'in',
  transferOut: 'out',
  collection: 'in',
  payment: 'out',
};

/**
 * `pendingApproval`: written, waiting on an approval policy — no effect on the balance or the ledger.
 * `recorded`: applied to the book balance and reported to the ledger. `reversed` keeps its effect and
 * is offset by the linked `reversal` movement.
 */
export const MOVEMENT_STATES = ['pendingApproval', 'recorded', 'rejected', 'reversed'] as const;
export const MovementStateSchema = z.enum(MOVEMENT_STATES);
export type MovementState = z.infer<typeof MovementStateSchema>;

export const TreasurySourceSchema = z.strictObject({
  type: z.string().regex(/^[a-z][A-Za-z]{1,39}$/),
  id: z.string().min(1).max(80),
  reference: z.string().max(80).optional(),
});
export type TreasurySource = z.infer<typeof TreasurySourceSchema>;

/**
 * How a treasury account's **opening balance** reaches the ledger:
 *
 * - `postingRule` — the opening reports the `treasury.opening` event and the posting rule posts it
 *   (the only behaviour before 2026-10-14, and still the default);
 * - `openingEntry` — the ledger opening entry (journal `opening`) carries the balance on the linked
 *   chart account; the treasury opening sets the book balance only and posts nothing.
 *
 * Exactly one of the two may carry a balance, or it would be in the ledger twice: each side refuses
 * when the other already does (`LEDGER_OPENING_ALREADY_POSTED`, `TREASURY_OPENING_POSTS_THIS_ACCOUNT`).
 */
export const OPENING_LEDGER_TREATMENTS = ['postingRule', 'openingEntry'] as const;
export const OpeningLedgerTreatmentSchema = z.enum(OPENING_LEDGER_TREATMENTS);
export type OpeningLedgerTreatment = z.infer<typeof OpeningLedgerTreatmentSchema>;

export const TreasuryMovementSchema = z.strictObject({
  movementId: RecordIdSchema,
  voucherNumber: z.string().optional(),
  accountId: RecordIdSchema,
  kind: MovementKindSchema,
  direction: MovementDirectionSchema,
  amount: MoneySchema,
  movementDate: BusinessDateSchema,
  description: z.string(),
  /** The bank's or the slip's own reference — a deposit-slip number. Shown verbatim. */
  reference: z.string().optional(),
  /** The other treasury account of a transfer. */
  counterAccountId: RecordIdSchema.optional(),
  /** The chart account on the other side, when the person knows it — a context account for posting. */
  counterLedgerAccountId: RecordIdSchema.optional(),
  /** An opening's ledger treatment (absent on an older opening: it posted through the rule). */
  openingLedgerTreatment: OpeningLedgerTreatmentSchema.optional(),
  /** The record that caused the movement: a receipt, a transfer, a payment request, a statement line. */
  source: TreasurySourceSchema.optional(),
  state: MovementStateSchema,
  approvalRequestId: z.string().optional(),
  reversesMovementId: RecordIdSchema.optional(),
  reversedByMovementId: RecordIdSchema.optional(),
  reversalReason: z.string().optional(),
  /** The account's book balance right after this movement was applied. */
  balanceAfter: MoneySchema.optional(),
  recordedByAccountId: z.string(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type TreasuryMovement = z.infer<typeof TreasuryMovementSchema>;

const IdempotencyKeySchema = z.string().trim().min(8).max(100);

export const RecordMovementSchema = z.strictObject({
  accountId: RecordIdSchema,
  kind: z.enum(RECORDABLE_MOVEMENT_KINDS),
  amount: PositiveMoneySchema,
  movementDate: BusinessDateSchema,
  description: z.string().trim().min(3).max(300),
  reference: z.string().trim().max(80).optional(),
  counterLedgerAccountId: RecordIdSchema.optional(),
  /** Only for `kind: 'opening'`; omitted means `postingRule`. */
  openingLedgerTreatment: OpeningLedgerTreatmentSchema.optional(),
  idempotencyKey: IdempotencyKeySchema,
});
export type RecordMovement = z.infer<typeof RecordMovementSchema>;

export const ReverseTreasurySchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  reversalDate: BusinessDateSchema,
  idempotencyKey: IdempotencyKeySchema,
});
export type ReverseTreasury = z.infer<typeof ReverseTreasurySchema>;

export const MovementQuerySchema = z.strictObject({
  accountId: RecordIdSchema.optional(),
  kind: MovementKindSchema.optional(),
  state: MovementStateSchema.optional(),
  from: BusinessDateSchema.optional(),
  to: BusinessDateSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).max(200).optional(),
});
export type MovementQuery = z.infer<typeof MovementQuerySchema>;

export const MovementPageSchema = z.strictObject({
  items: z.array(TreasuryMovementSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});
export type MovementPage = z.infer<typeof MovementPageSchema>;

/** What a money operation answers: the record, and warnings a person should read. */
export const TREASURY_WARNINGS = [
  /** The bank account's balance is now below zero, inside its approved overdraft facility (`BD-55`). */
  'negativeBankBalance',
  /** No chart account is linked to the treasury account, so a rule using it will wait (`BD-37`). */
  'ledgerAccountNotLinked',
  /** Waiting on an approval policy; nothing is applied yet. */
  'awaitingApproval',
  /** The cashbox now holds more than its configured maximum balance (`BD-41`): deposit the excess. */
  'cashAboveLimit',
] as const;
export const TreasuryWarningSchema = z.enum(TREASURY_WARNINGS);
export type TreasuryWarning = z.infer<typeof TreasuryWarningSchema>;

export const MovementResultSchema = z.strictObject({
  movement: TreasuryMovementSchema,
  warnings: z.array(TreasuryWarningSchema),
});
export type MovementResult = z.infer<typeof MovementResultSchema>;

/* ------------------------------------------------------------------- transfers */

export const TRANSFER_STATES = [
  'pendingApproval',
  'completed',
  'rejected',
  'cancelled',
  'reversed',
] as const;
export const TransferStateSchema = z.enum(TRANSFER_STATES);
export type TransferState = z.infer<typeof TransferStateSchema>;

export const TreasuryTransferSchema = z.strictObject({
  transferId: RecordIdSchema,
  transferNumber: z.string().optional(),
  fromAccountId: RecordIdSchema,
  toAccountId: RecordIdSchema,
  amount: MoneySchema,
  transferDate: BusinessDateSchema,
  description: z.string(),
  reference: z.string().optional(),
  state: TransferStateSchema,
  approvalRequestId: z.string().optional(),
  outMovementId: RecordIdSchema.optional(),
  inMovementId: RecordIdSchema.optional(),
  requestedByAccountId: z.string(),
  reversalReason: z.string().optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type TreasuryTransfer = z.infer<typeof TreasuryTransferSchema>;

export const CreateTransferSchema = z
  .strictObject({
    fromAccountId: RecordIdSchema,
    toAccountId: RecordIdSchema,
    amount: PositiveMoneySchema,
    transferDate: BusinessDateSchema,
    description: z.string().trim().min(3).max(300),
    reference: z.string().trim().max(80).optional(),
    idempotencyKey: IdempotencyKeySchema,
  })
  .refine((value) => value.fromAccountId !== value.toAccountId, {
    message: 'SAME_ACCOUNT',
    path: ['toAccountId'],
  });
export type CreateTransfer = z.infer<typeof CreateTransferSchema>;

export const CancelTreasurySchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type CancelTreasury = z.infer<typeof CancelTreasurySchema>;

export const TransferQuerySchema = z.strictObject({
  accountId: RecordIdSchema.optional(),
  state: TransferStateSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).max(200).optional(),
});
export type TransferQuery = z.infer<typeof TransferQuerySchema>;

export const TransferPageSchema = z.strictObject({
  items: z.array(TreasuryTransferSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});

export const TransferResultSchema = z.strictObject({
  transfer: TreasuryTransferSchema,
  warnings: z.array(TreasuryWarningSchema),
});
export type TransferResult = z.infer<typeof TransferResultSchema>;

/* ---------------------------------------------------------------------- payees */

/**
 * Someone the company pays who is not yet a vendor or an employee record. Their bank details are
 * encrypted and masked like the company's own; **a change of bank details goes through approval**
 * (`FIN-CASH-005`, `G-05`) — the classic fraud is a changed IBAN on an otherwise genuine payee.
 */
/**
 * `creation`: the bank details a new payee was created with, awaiting their first approval (BD-60).
 * `change` (or absent, before 2026-10-07): a change to approved details.
 */
export const PAYEE_CHANGE_KINDS = ['creation', 'change'] as const;

export const PayeeChangeSchema = z.strictObject({
  kind: z.enum(PAYEE_CHANGE_KINDS).optional(),
  bankName: z.string().optional(),
  accountNumberMasked: z.string().optional(),
  ibanMasked: z.string().optional(),
  reason: z.string(),
  requestedByAccountId: z.string(),
  requestedAt: InstantSchema,
  approvalRequestId: z.string(),
});

export const PayeeSchema = z.strictObject({
  payeeId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  name: z.string(),
  bankName: z.string().optional(),
  accountNumberMasked: z.string().optional(),
  ibanMasked: z.string().optional(),
  pendingChange: PayeeChangeSchema.optional(),
  state: TreasuryRecordStateSchema,
  createdByAccountId: z.string(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Payee = z.infer<typeof PayeeSchema>;

/**
 * A payee is created at once; **bank details given with it wait for approval** like any later change
 * (BD-60, `treasury.payee.change`) and need a reason. Without an approval policy, a payee with bank
 * details is refused; one without (paid in cash) is not.
 */
export const CreatePayeeSchema = z
  .strictObject({
    legalEntityId: RecordIdSchema,
    name: EnteredNameSchema,
    bankName: z.string().trim().min(2).max(120).optional(),
    accountNumber: BankIdentifierInputSchema.optional(),
    iban: BankIdentifierInputSchema.optional(),
    reason: z.string().trim().min(3).max(500).optional(),
  })
  .refine(
    (value) =>
      (value.bankName === undefined &&
        value.accountNumber === undefined &&
        value.iban === undefined) ||
      value.reason !== undefined,
    { message: 'REASON_REQUIRED', path: ['reason'] },
  );
export type CreatePayee = z.infer<typeof CreatePayeeSchema>;

export const RequestPayeeChangeSchema = z
  .strictObject({
    bankName: z.string().trim().min(2).max(120).optional(),
    accountNumber: BankIdentifierInputSchema.optional(),
    iban: BankIdentifierInputSchema.optional(),
    reason: z.string().trim().min(3).max(500),
    expectedVersion: z.number().int().positive(),
  })
  .refine(
    (value) =>
      value.bankName !== undefined || value.accountNumber !== undefined || value.iban !== undefined,
    { message: 'NOTHING_TO_UPDATE' },
  );
export type RequestPayeeChange = z.infer<typeof RequestPayeeChangeSchema>;

export const PayeeListSchema = z.strictObject({ items: z.array(PayeeSchema) });

/* ------------------------------------------------------------ payment requests */

export const PAYMENT_REQUEST_STATES = [
  'pendingApproval',
  'approved',
  'executed',
  'rejected',
  'cancelled',
  /** Executed, then reversed because the money came back (COL-REFUND-002). */
  'reversed',
] as const;
export const PaymentRequestStateSchema = z.enum(PAYMENT_REQUEST_STATES);
export type PaymentRequestState = z.infer<typeof PaymentRequestStateSchema>;

export const OUTGOING_PAYMENT_METHODS = ['bankTransfer', 'cash', 'cheque'] as const;
export const OutgoingPaymentMethodSchema = z.enum(OUTGOING_PAYMENT_METHODS);
export type OutgoingPaymentMethod = z.infer<typeof OutgoingPaymentMethodSchema>;

export const PaymentExecutionSchema = z.strictObject({
  executedOn: BusinessDateSchema,
  reference: z.string(),
  executedByAccountId: z.string(),
  movementId: RecordIdSchema.optional(),
  chequeId: RecordIdSchema.optional(),
});

/** What a payment settles. Only vendor invoices for now; the owning module settles it on execution. */
export const PaymentSourceSchema = z.strictObject({
  type: z.enum([
    'vendorInvoice',
    'vendorAdvance',
    'contractorCertificate',
    'contractorAdvance',
    'contractorRetention',
    'employeeAdvance',
    'expense',
    'cashAdvance',
    /** A customer refund (collections): its execution settles the refund, posted `refund.paid`. */
    'customerRefund',
    /** A contract-linked commission (sales): its execution marks the commission paid. */
    'salesCommission',
  ]),
  id: RecordIdSchema,
});
export type PaymentSource = z.infer<typeof PaymentSourceSchema>;

export const PaymentRequestSchema = z.strictObject({
  paymentRequestId: RecordIdSchema,
  requestNumber: z.string().optional(),
  fromAccountId: RecordIdSchema,
  payeeId: RecordIdSchema,
  payeeName: z.string(),
  amount: MoneySchema,
  purpose: z.string(),
  method: OutgoingPaymentMethodSchema,
  state: PaymentRequestStateSchema,
  approvalRequestId: z.string().optional(),
  requestedByAccountId: z.string(),
  execution: PaymentExecutionSchema.optional(),
  /** The money of an executed payment came back: a contra-movement, the posting reversed. */
  reversal: z
    .strictObject({
      reversedOn: BusinessDateSchema,
      reason: z.string(),
      reversedByAccountId: z.string(),
      movementId: RecordIdSchema.optional(),
    })
    .optional(),
  cancellationReason: z.string().optional(),
  /** The business record this payment settles, e.g. a vendor invoice (BMP-2 package 4). */
  source: PaymentSourceSchema.optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type PaymentRequest = z.infer<typeof PaymentRequestSchema>;

export const CreatePaymentRequestSchema = z.strictObject({
  fromAccountId: RecordIdSchema,
  payeeId: RecordIdSchema,
  amount: PositiveMoneySchema,
  purpose: z.string().trim().min(3).max(500),
  method: OutgoingPaymentMethodSchema,
  idempotencyKey: IdempotencyKeySchema,
});
export type CreatePaymentRequest = z.infer<typeof CreatePaymentRequestSchema>;

/**
 * Evidence that an approved payment was made outside the product. A cheque payment issues an outgoing
 * cheque instead: the money leaves the bank when the cheque clears.
 */
/**
 * Reverse an executed payment whose money came back to the account (a returned transfer, cash handed
 * back). Not for money a customer repays later — that is a new receipt.
 */
export const ReversePaymentSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  reversalDate: BusinessDateSchema,
  expectedVersion: z.number().int().positive(),
});
export type ReversePayment = z.infer<typeof ReversePaymentSchema>;

export const ExecutePaymentSchema = z
  .strictObject({
    executedOn: BusinessDateSchema,
    reference: z.string().trim().min(2).max(120),
    chequeNumber: z.string().trim().min(1).max(60).optional(),
    chequeDueOn: BusinessDateSchema.optional(),
    expectedVersion: z.number().int().positive(),
  })
  .refine((value) => (value.chequeNumber === undefined) === (value.chequeDueOn === undefined), {
    message: 'CHEQUE_DETAILS_INCOMPLETE',
    path: ['chequeDueOn'],
  });
export type ExecutePayment = z.infer<typeof ExecutePaymentSchema>;

export const PaymentRequestQuerySchema = z.strictObject({
  state: PaymentRequestStateSchema.optional(),
  accountId: RecordIdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).max(200).optional(),
});
export type PaymentRequestQuery = z.infer<typeof PaymentRequestQuerySchema>;

export const PaymentRequestPageSchema = z.strictObject({
  items: z.array(PaymentRequestSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});

/* ------------------------------------------------------------- outgoing cheques */

/** An outgoing cheque's own lifecycle (`COL-CHECK-004`). The bank pays it when it clears. */
export const OUTGOING_CHEQUE_STATES = ['issued', 'delivered', 'cleared', 'cancelled'] as const;
export const OutgoingChequeStateSchema = z.enum(OUTGOING_CHEQUE_STATES);
export type OutgoingChequeState = z.infer<typeof OutgoingChequeStateSchema>;

export const OUTGOING_CHEQUE_TRANSITIONS: Readonly<
  Record<OutgoingChequeState, readonly OutgoingChequeState[]>
> = {
  issued: ['delivered', 'cancelled'],
  delivered: ['cleared', 'cancelled'],
  cleared: [],
  cancelled: [],
};

export const OutgoingChequeSchema = z.strictObject({
  chequeId: RecordIdSchema,
  chequeNumber: z.string(),
  accountId: RecordIdSchema,
  payeeId: RecordIdSchema,
  payeeName: z.string(),
  amount: MoneySchema,
  issuedOn: BusinessDateSchema,
  dueOn: BusinessDateSchema,
  paymentRequestId: RecordIdSchema,
  state: OutgoingChequeStateSchema,
  deliveredOn: BusinessDateSchema.optional(),
  clearedOn: BusinessDateSchema.optional(),
  movementId: RecordIdSchema.optional(),
  cancellationReason: z.string().optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type OutgoingCheque = z.infer<typeof OutgoingChequeSchema>;

export const OutgoingChequeActionSchema = z
  .strictObject({
    action: z.enum(['deliver', 'clear', 'cancel']),
    on: BusinessDateSchema,
    reason: z.string().trim().min(3).max(500).optional(),
    expectedVersion: z.number().int().positive(),
  })
  .refine((value) => value.action !== 'cancel' || value.reason !== undefined, {
    message: 'REASON_REQUIRED',
    path: ['reason'],
  });
export type OutgoingChequeAction = z.infer<typeof OutgoingChequeActionSchema>;

export const OutgoingChequeListSchema = z.strictObject({ items: z.array(OutgoingChequeSchema) });

/* -------------------------------------------------------------- reconciliation */

export const STATEMENT_STATES = ['open', 'submitted', 'reconciled'] as const;
export const StatementStateSchema = z.enum(STATEMENT_STATES);
export type StatementState = z.infer<typeof StatementStateSchema>;

/** As the bank reports it: a credit is money into the account, a debit is money out. */
export const STATEMENT_LINE_DIRECTIONS = ['credit', 'debit'] as const;
export const StatementLineDirectionSchema = z.enum(STATEMENT_LINE_DIRECTIONS);
export type StatementLineDirection = z.infer<typeof StatementLineDirectionSchema>;

export const MATCH_STATES = ['unmatched', 'partial', 'matched'] as const;
export const MatchStateSchema = z.enum(MATCH_STATES);
export type MatchState = z.infer<typeof MatchStateSchema>;

export const BankStatementSchema = z.strictObject({
  statementId: RecordIdSchema,
  accountId: RecordIdSchema,
  reference: z.string(),
  periodFrom: BusinessDateSchema,
  periodTo: BusinessDateSchema,
  openingBalance: MoneySchema,
  closingBalance: MoneySchema,
  state: StatementStateSchema,
  lineCount: z.number().int().nonnegative(),
  preparedByAccountId: z.string(),
  submittedByAccountId: z.string().optional(),
  submittedAt: InstantSchema.optional(),
  approvedByAccountId: z.string().optional(),
  approvedAt: InstantSchema.optional(),
  approvalRequestId: z.string().optional(),
  rejectionReason: z.string().optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type BankStatement = z.infer<typeof BankStatementSchema>;

export const CreateStatementSchema = z
  .strictObject({
    accountId: RecordIdSchema,
    reference: z.string().trim().min(1).max(80),
    periodFrom: BusinessDateSchema,
    periodTo: BusinessDateSchema,
    openingBalance: DecimalStringSchema,
    closingBalance: DecimalStringSchema,
  })
  .refine((value) => value.periodFrom <= value.periodTo, {
    message: 'RANGE_REVERSED',
    path: ['periodTo'],
  });
export type CreateStatement = z.infer<typeof CreateStatementSchema>;

export const StatementLineInputSchema = z.strictObject({
  valueDate: BusinessDateSchema,
  description: z.string().trim().min(1).max(300),
  reference: z.string().trim().max(80).optional(),
  direction: StatementLineDirectionSchema,
  amount: DecimalStringSchema.refine(
    (value) => !value.startsWith('-') && !/^0(\.0+)?$/.test(value),
    { message: 'POSITIVE_AMOUNT_EXPECTED' },
  ),
});
export type StatementLineInput = z.infer<typeof StatementLineInputSchema>;

export const AddStatementLinesSchema = z.strictObject({
  lines: z.array(StatementLineInputSchema).min(1).max(500),
  expectedVersion: z.number().int().positive(),
});
export type AddStatementLines = z.infer<typeof AddStatementLinesSchema>;

export const StatementLineSchema = z.strictObject({
  lineId: RecordIdSchema,
  statementId: RecordIdSchema,
  lineNo: z.number().int().positive(),
  valueDate: BusinessDateSchema,
  description: z.string(),
  reference: z.string().optional(),
  direction: StatementLineDirectionSchema,
  amount: MoneySchema,
  matchedAmount: MoneySchema,
  matchState: MatchStateSchema,
  source: z.enum(['manual', 'import']),
});
export type StatementLine = z.infer<typeof StatementLineSchema>;

export const MatchPartSchema = z.strictObject({ id: RecordIdSchema, amount: MoneySchema });

export const ReconciliationMatchSchema = z.strictObject({
  matchId: RecordIdSchema,
  statementId: RecordIdSchema,
  lines: z.array(MatchPartSchema),
  movements: z.array(MatchPartSchema),
  /** Statement side minus book side, signed; zero unless inside a configured tolerance (`BD-40`). */
  difference: MoneySchema,
  /** A value date further from the book date than the configured tolerance; needs a reason. */
  dateException: z.boolean(),
  reason: z.string().optional(),
  state: z.enum(['active', 'undone']),
  createdByAccountId: z.string(),
  createdAt: InstantSchema,
  undoneByAccountId: z.string().optional(),
  undoneAt: InstantSchema.optional(),
  undoReason: z.string().optional(),
});
export type ReconciliationMatch = z.infer<typeof ReconciliationMatchSchema>;

const MatchPartInputSchema = z.strictObject({
  id: RecordIdSchema,
  /** Omitted: whatever of the item is still unmatched. */
  amount: DecimalStringSchema.optional(),
});

export const CreateMatchSchema = z.strictObject({
  lines: z.array(MatchPartInputSchema).min(1).max(50),
  movements: z.array(MatchPartInputSchema).min(1).max(50),
  reason: z.string().trim().min(3).max(500).optional(),
  expectedVersion: z.number().int().positive(),
});
export type CreateMatch = z.infer<typeof CreateMatchSchema>;

export const UndoMatchSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type UndoMatch = z.infer<typeof UndoMatchSchema>;

/** Record what the statement shows and the books do not — a bank charge — and match it in one step. */
export const RecordFromLineSchema = z.strictObject({
  lineId: RecordIdSchema,
  kind: z.enum(['bankCharge', 'bankInterest', 'deposit', 'withdrawal']),
  description: z.string().trim().min(3).max(300).optional(),
  counterLedgerAccountId: RecordIdSchema.optional(),
  expectedVersion: z.number().int().positive(),
});
export type RecordFromLine = z.infer<typeof RecordFromLineSchema>;

export const StatementVersionSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
});

export const RejectStatementSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});

export const BookItemSchema = z.strictObject({
  movement: TreasuryMovementSchema,
  matchedAmount: MoneySchema,
  matchState: MatchStateSchema,
});
export type BookItem = z.infer<typeof BookItemSchema>;

export const ReconciliationSummarySchema = z.strictObject({
  /** Closing minus opening as the statement states it. */
  statementMovement: MoneySchema,
  /** The sum of the lines entered, signed. Must equal `statementMovement` before submission. */
  linesNet: MoneySchema,
  linesComplete: z.boolean(),
  unmatchedLines: z.number().int().nonnegative(),
  /** Book movements in the period not (fully) matched — outstanding items, both directions. */
  outstandingIn: MoneySchema,
  outstandingOut: MoneySchema,
  /** The account's book balance at the end of the period. */
  bookBalanceAtEnd: MoneySchema,
  /** True when every line is fully matched and the lines add up to the statement. */
  readyToSubmit: z.boolean(),
});
export type ReconciliationSummary = z.infer<typeof ReconciliationSummarySchema>;

export const ReconciliationWorkspaceSchema = z.strictObject({
  statement: BankStatementSchema,
  lines: z.array(StatementLineSchema),
  matches: z.array(ReconciliationMatchSchema),
  bookItems: z.array(BookItemSchema),
  summary: ReconciliationSummarySchema,
  /**
   * The approved matching rule (`BD-40`, 2026-10-07): amounts agree exactly — zero tolerance — and any
   * date difference needs a written reason the reviewer sees. Not configurable.
   */
  tolerance: z.strictObject({
    rule: z.literal('exactAmount'),
    decision: z.literal('BD-40'),
  }),
});
export type ReconciliationWorkspace = z.infer<typeof ReconciliationWorkspaceSchema>;

export const StatementQuerySchema = z.strictObject({
  accountId: RecordIdSchema.optional(),
  state: StatementStateSchema.optional(),
});
export const StatementListSchema = z.strictObject({ items: z.array(BankStatementSchema) });

/* --------------------------------------------------------------------- report */

export const TreasuryPositionQuerySchema = z.strictObject({
  legalEntityId: RecordIdSchema.optional(),
  horizonDays: z.coerce.number().int().min(7).max(180).default(30),
});
export type TreasuryPositionQuery = z.infer<typeof TreasuryPositionQuerySchema>;

export const PositionBucketSchema = z.strictObject({
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  inflows: DecimalStringSchema,
  outflows: DecimalStringSchema,
  /** Book balance plus everything expected up to the end of this bucket. */
  projected: DecimalStringSchema,
});

export const TreasuryPositionSchema = z.strictObject({
  generatedAt: InstantSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  asOf: BusinessDateSchema,
  horizonDays: z.number().int().positive(),
  accounts: z.array(
    z.strictObject({
      accountId: RecordIdSchema,
      code: z.string(),
      name: LocalizedLabelSchema,
      kind: TreasuryAccountKindSchema,
      bookBalance: DecimalStringSchema,
    }),
  ),
  totals: z.strictObject({
    bank: DecimalStringSchema,
    cash: DecimalStringSchema,
    total: DecimalStringSchema,
  }),
  expected: z.strictObject({
    /** Instalments falling due in the horizon, not yet paid. */
    instalments: DecimalStringSchema,
    /** Incoming cheques and notes in custody falling due in the horizon. */
    instruments: DecimalStringSchema,
    /** Approved payment requests not yet executed. */
    payments: DecimalStringSchema,
    /** Outgoing cheques issued and not yet cleared. */
    outgoingCheques: DecimalStringSchema,
  }),
  buckets: z.array(PositionBucketSchema),
  /** The forecast reads only what is in scope and recorded; it is never a complete cash plan. */
  partial: z.literal(true),
  /** True when a source had more rows than the report reads. */
  truncated: z.boolean(),
  scopeLimited: z.boolean(),
});
export type TreasuryPosition = z.infer<typeof TreasuryPositionSchema>;

/* ------------------------------------------------------------------ vocabulary */

/**
 * Operations treasury submits to the approval engine. Payments and payee bank-data changes **require**
 * a policy and are refused without one; the others are governed only where a policy names them.
 */
export const TREASURY_APPROVAL_OPERATIONS = {
  movement: 'treasury.movement',
  transfer: 'treasury.transfer',
  payment: 'treasury.payment',
  payeeChange: 'treasury.payee.change',
  reconciliation: 'treasury.reconciliation.close',
} as const;

export const TREASURY_AUDIT_ACTIONS = {
  bankCreated: 'treasury.bank.created',
  bankUpdated: 'treasury.bank.updated',
  bankBranchAdded: 'treasury.bank.branchAdded',
  bankStateChanged: 'treasury.bank.stateChanged',
  accountCreated: 'treasury.account.created',
  accountUpdated: 'treasury.account.updated',
  accountStateChanged: 'treasury.account.stateChanged',
  identifiersRevealed: 'treasury.account.identifiersRevealed',
  movementRecorded: 'treasury.movement.recorded',
  movementRequested: 'treasury.movement.requested',
  movementRejected: 'treasury.movement.rejected',
  movementReversed: 'treasury.movement.reversed',
  movementRefused: 'treasury.movement.refused',
  transferRequested: 'treasury.transfer.requested',
  transferCompleted: 'treasury.transfer.completed',
  transferRejected: 'treasury.transfer.rejected',
  transferCancelled: 'treasury.transfer.cancelled',
  transferReversed: 'treasury.transfer.reversed',
  payeeCreated: 'treasury.payee.created',
  payeeChangeRequested: 'treasury.payee.changeRequested',
  payeeChangeApplied: 'treasury.payee.changeApplied',
  payeeChangeRejected: 'treasury.payee.changeRejected',
  payeeIdentifiersRevealed: 'treasury.payee.identifiersRevealed',
  paymentRequested: 'treasury.payment.requested',
  paymentApproved: 'treasury.payment.approved',
  paymentRejected: 'treasury.payment.rejected',
  paymentExecuted: 'treasury.payment.executed',
  paymentReversed: 'treasury.payment.reversed',
  paymentCancelled: 'treasury.payment.cancelled',
  chequeOutChanged: 'treasury.chequeOut.stateChanged',
  statementCreated: 'treasury.statement.created',
  statementLinesAdded: 'treasury.statement.linesAdded',
  statementMatched: 'treasury.statement.matched',
  statementMatchUndone: 'treasury.statement.matchUndone',
  statementSubmitted: 'treasury.statement.submitted',
  statementReconciled: 'treasury.statement.reconciled',
  statementRejected: 'treasury.statement.rejected',
} as const;
